import React, { useState, useEffect } from "react";
import {
  Layout,
  Button,
  Typography,
  Form,
  Input,
  Divider,
  message,
  Checkbox,
  Modal, // Added Modal for map
  Row,
  Col,
  Card, // Added Card for map display
} from "antd";
import {
  EnvironmentOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import signinLogo from "../../assets/images/logo-for-logins.png";
import GoogleIcon from "../../assets/images/Google_login_icon.svg";
import AppleIcon from "../../assets/images/Google_login_icon.svg";
import { useNavigate } from "react-router";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";

// Map component - you can use Google Maps, Mapbox, or Leaflet
// Here's an example with Google Maps (you'll need to install google-map-react)
// npm install google-map-react
import GoogleMapReact from "google-map-react";

const { Content } = Layout;
const { Title, Text } = Typography;

// Marker component for the map
const MapMarker = ({ lat, lng, text }) => (
  <div style={{
    position: 'absolute',
    transform: 'translate(-50%, -100%)',
    color: '#1890ff',
    fontSize: '24px',
    cursor: 'pointer'
  }}>
    📍
    <div style={{
      position: 'absolute',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      whiteSpace: 'nowrap',
      backgroundColor: 'white',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    }}>
      {text}
    </div>
  </div>
);

const Register = () => {
  const { request } = useRequest();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const [searchLocation, setSearchLocation] = useState("");
  const [selectedLocation, setSelectedLocation] = useState({
    lat: null,
    lng: null,
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: ""
  });
  const [mapCenter, setMapCenter] = useState({
    lat: 40.7128,
    lng: -74.0060
  });

  // Default location (New York)
  const defaultCenter = {
    lat: 40.7128,
    lng: -74.0060
  };

  // Initialize map with user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter({ lat: latitude, lng: longitude });
          setSelectedLocation(prev => ({
            ...prev,
            lat: latitude,
            lng: longitude
          }));
        },
        (error) => {
          console.log("Geolocation error:", error);
          // Use default location
          setMapCenter(defaultCenter);
        }
      );
    }
  }, []);

  // Function to handle map click
  const handleMapClick = async ({ lat, lng }) => {
    try {
      // Reverse geocode to get address from coordinates
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      
      if (data) {
        const address = data.display_name || "";
        const addressParts = data.address || {};
        
        setSelectedLocation({
          lat,
          lng,
          address: address,
          city: addressParts.city || addressParts.town || addressParts.village || "",
          state: addressParts.state || "",
          country: addressParts.country || "",
          postalCode: addressParts.postcode || ""
        });

        // Update form fields
        form.setFieldsValue({
          location: address,
          city: addressParts.city || addressParts.town || addressParts.village || "",
          state: addressParts.state || "",
          country: addressParts.country || "",
          postalCode: addressParts.postcode || "",
          latitude: lat,
          longitude: lng
        });
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      // Still set the coordinates even if reverse geocoding fails
      setSelectedLocation(prev => ({
        ...prev,
        lat,
        lng,
        address: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`
      }));
      
      form.setFieldsValue({
        location: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`,
        latitude: lat,
        longitude: lng
      });
    }
  };

  // Function to search location
  const handleSearchLocation = async () => {
    if (!searchLocation.trim()) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchLocation)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lon);
        
        setMapCenter({ lat: parsedLat, lng: parsedLng });
        handleMapClick({ lat: parsedLat, lng: parsedLng });
        setSearchLocation("");
      } else {
        ShowToast("Location not found", Severty.ERROR);
      }
    } catch (error) {
      console.error("Error searching location:", error);
      ShowToast("Error searching location", Severty.ERROR);
    }
  };

  const handleSignUp = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name: values.fullName,
        franchise_id: values.franchiseId,
        email: values.email,
        password: values.password,
        location: values.location || "",
        city: values.city || "",
        state: values.state || "",
        country: values.country || "",
        postal_code: values.postalCode || "",
        latitude: values.latitude || null,
        longitude: values.longitude || null
      };

      await request({
        url: apiPath.signUp,
        method: "POST",
        data: payload,
        onSuccess: (res) => {
          if (res.status === 201 || res.status === 200) {
            ShowToast(
              res.message || "Account created successfully!",
              Severty.SUCCESS
            );

            localStorage.setItem("email", values.email);

            navigate("/otp", {
              state: { email: values.email },
            });
          } else {
            ShowToast(res.message || "Sign up failed", Severty.ERROR);
          }
        },
        onError: (error) => {
          ShowToast(
            error.response?.data?.message || "Sign up failed",
            Severty.ERROR
          );
        },
      });
    } catch (err) {
      console.error(err);
      ShowToast("Something went wrong!", Severty.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-new-login-page-designe-divv">
      <Layout className="layout-default layout-signin">
        <Content className="signin">
          <div className="contant-bg-sign">
            <div className="signup-box signup-page">
              {/* Logo */}
              <div className="signup-logo">
                <img src={signinLogo} alt="logo" />
              </div>

              <Form
                form={form}
                layout="vertical"
                className="sign_in_formm"
                onFinish={handleSignUp}
              >
                <div className="login_header-mian">
                  <h4>CREATE AN ACCOUNT</h4>
                </div>

                <Form.Item
                  name="fullName"
                  label="Full name"
                  rules={[
                    { required: true, message: "Please enter your full name!" },
                    { whitespace: true, message: "Name cannot be only spaces!" },
                    {
                      pattern: /^(?!\s)(?!.*\s{2,})([A-Za-z\s]+)(?<!\s)$/,
                      message: "Name can only contain letters and single spaces",
                    },
                  ]}
                >
                  <Input placeholder="Full name" size="large" style={{ borderRadius: "8px" }} />
                </Form.Item>

                <Form.Item
                  name="franchiseId"
                  label="Gym Name"
                  rules={[
                    { required: true, message: "Please enter gym name!" },
                    { whitespace: true, message: "Gym name cannot be only spaces!" },
                  ]}
                >
                  <Input placeholder="Gym Name" size="large" style={{ borderRadius: "8px" }} />
                </Form.Item>

                {/* Location Section */}
                <div style={{ marginBottom: '24px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Gym Location
                  </Text>
                  
                  {/* Map Preview Card */}
                  <Card
                    hoverable
                    onClick={() => setMapModalVisible(true)}
                    style={{
                      marginBottom: '16px',
                      borderRadius: '8px',
                      border: '1px solid #d9d9d9',
                      cursor: 'pointer'
                    }}
                  >
                    <Row align="middle" gutter={[16, 16]}>
                      <Col xs={24} md={16}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <EnvironmentOutlined style={{ color: '#1890ff' }} />
                          <div>
                            <Text strong>Select Location on Map</Text>
                            <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                              Click to open map and select your gym location
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                        <Button type="primary" ghost>
                          Select on Map
                        </Button>
                      </Col>
                    </Row>
                  </Card>

                  {/* Hidden Location Fields */}
                  <Form.Item name="location" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="latitude" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="longitude" hidden>
                    <Input />
                  </Form.Item>

                  {/* Location Details - Visible when location is selected */}
                  <Form.Item
                    name="city"
                    label="City"
                    rules={[
                      { required: false },
                      { whitespace: true, message: "City cannot be only spaces!" },
                    ]}
                  >
                    <Input 
                      placeholder="City" 
                      size="large" 
                      style={{ borderRadius: "8px" }} 
                      disabled={!selectedLocation.address}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="state"
                        label="State/Province"
                        rules={[
                          { required: false },
                          { whitespace: true, message: "State cannot be only spaces!" },
                        ]}
                      >
                        <Input 
                          placeholder="State/Province" 
                          size="large" 
                          style={{ borderRadius: "8px" }} 
                          disabled={!selectedLocation.address}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="postalCode"
                        label="Postal Code"
                        rules={[
                          { required: false },
                          { whitespace: true, message: "Postal code cannot be only spaces!" },
                        ]}
                      >
                        <Input 
                          placeholder="Postal Code" 
                          size="large" 
                          style={{ borderRadius: "8px" }} 
                          disabled={!selectedLocation.address}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="country"
                    label="Country"
                    rules={[
                      { required: false },
                      { whitespace: true, message: "Country cannot be only spaces!" },
                    ]}
                  >
                    <Input 
                      placeholder="Country" 
                      size="large" 
                      style={{ borderRadius: "8px" }} 
                      disabled={!selectedLocation.address}
                    />
                  </Form.Item>

                  {/* Selected Location Preview */}
                  {selectedLocation.address && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: '6px',
                      marginTop: '8px'
                    }}>
                      <Text type="success">
                        <EnvironmentOutlined /> Selected: {selectedLocation.address}
                      </Text>
                    </div>
                  )}
                </div>

                {/* Email Field */}
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, type: "email", message: "Please enter a valid email!" },
                    { whitespace: true, message: "Email cannot be only spaces!" },
                  ]}
                >
                  <Input placeholder="Email" size="large" style={{ borderRadius: "8px" }} />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Enter password"
                  rules={[
                    { required: true, message: "Please enter password!" },
                    { whitespace: true, message: "Password cannot be only spaces!" },
                    {
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*]).{8,}$/,
                      message:
                        "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character",
                    },
                  ]}
                >
                  <Input.Password
                    placeholder="Enter password"
                    size="large"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="Confirm password"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm your password!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match!"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Confirm password"
                    size="large"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Item>

                {/* Terms & Conditions Checkbox - Required */}
                <Form.Item
                  name="acceptTerms"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(new Error("You must accept the Terms of Service and Privacy Policy")),
                    },
                  ]}
                >
                  <Checkbox>
                    I agree to Fusionhub{" "}
                    <span
                      style={{ color: "#1890ff", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/terms-and-conditions");
                      }}
                    >
                      Terms of Service
                    </span>{" "}
                    and{" "}
                    <span
                      style={{ color: "#1890ff", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/privacy-policy");
                      }}
                    >
                      Privacy Policy
                    </span>
                  </Checkbox>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="fill-button"
                    loading={loading}
                    block
                  >
                    Sign up
                  </Button>
                </Form.Item>
              </Form>

              <div className="already_have_account" style={{ textAlign: "center" }}>
                Already have a provider account?{" "}
                <span
                  style={{ color: "#1890ff", cursor: "pointer" }}
                  onClick={() => navigate("/login")}
                >
                  Sign in
                </span>
              </div>
            </div>
          </div>
        </Content>
      </Layout>

      {/* Map Selection Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EnvironmentOutlined />
            <span>Select Gym Location</span>
          </div>
        }
        open={mapModalVisible}
        onCancel={() => setMapModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setMapModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            onClick={() => setMapModalVisible(false)}
            disabled={!selectedLocation.address}
          >
            Confirm Location
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Search location (e.g., New York, London, Tokyo)"
            value={searchLocation}
            onChange={(e) => setSearchLocation(e.target.value)}
            onPressEnter={handleSearchLocation}
            suffix={
              <SearchOutlined 
                onClick={handleSearchLocation}
                style={{ cursor: 'pointer', color: '#1890ff' }}
              />
            }
            size="large"
          />
        </div>

        {/* Map Container */}
        <div style={{ height: '400px', width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
          <GoogleMapReact
            bootstrapURLKeys={{ key: "AIzaSyDQ2DCe8qS4qVCkMtRZRnZZ_TF2qq1HSvs" }} // Replace with your Google Maps API key
            center={mapCenter}
            zoom={12}
            onClick={handleMapClick}
            options={{
              zoomControl: true,
              mapTypeControl: true,
              streetViewControl: false,
              fullscreenControl: true,
            }}
          >
            {selectedLocation.lat && selectedLocation.lng && (
              <MapMarker
                lat={selectedLocation.lat}
                lng={selectedLocation.lng}
                text="Your Gym Location"
              />
            )}
          </GoogleMapReact>
        </div>

        {/* Selected Location Details */}
        {selectedLocation.address && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f5ff', borderRadius: '6px' }}>
            <Text strong>Selected Location:</Text>
            <div style={{ color: '#595959' }}>
              {selectedLocation.address}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
              Coordinates: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '12px' }}>
          <Text type="secondary">
            Click on the map to select your gym location. You can also search for a location using the search bar above.
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default Register;